import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Eye, Lock, BarChart3, Brain } from 'lucide-react';
import apiService from '@/services/api';

interface PageContent {
  id: string;
  title: string;
  content: string;
  seoTitle?: string;
  seoDescription?: string;
}

const AboutPage = () => {
  const [pageContent, setPageContent] = useState<PageContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [useHardcodedFallback, setUseHardcodedFallback] = useState(false);

  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        const response = await apiService.get('/public/pages/about');
        if (response.success && response.data) {
          setPageContent(response.data);
          setError(null);
        } else {
          setError('Page content not found in database');
          setUseHardcodedFallback(true);
        }
      } catch (err) {
        console.error('Error fetching About page content:', err);
        setError('Failed to load page content from database. Using default content.');
        setUseHardcodedFallback(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPageContent();
  }, []);

  // Determine what title/meta to use
  const seoTitle = pageContent?.seoTitle || 'About Virewire | The Platform Redefining Property Services';
  const seoDescription = pageContent?.seoDescription || 'Learn about Virewire\'s mission to redefine property services management with institutional standards, accountability, and intelligence.';

  if (useHardcodedFallback || !pageContent) {
    return (
      <Layout>
        <Helmet>
          <title>{seoTitle}</title>
          <meta name="description" content={seoDescription} />
          <link rel="canonical" href="https://virewire.co.uk/about" />
        </Helmet>

      {/* Hero Section */}
      <section className="bg-gray-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-teal-600 font-semibold uppercase tracking-wide text-sm mb-4">About Our Story</p>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            The Platform Redefining<br />Property Services
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            A sophisticated managed marketplace reimagining how property care happens in the residential, landlord, and agents segments.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-teal-700 hover:bg-teal-800 text-white px-8 py-3 text-lg font-semibold" asChild>
              <Link to="/how-it-works">How It Works</Link>
            </Button>
            <Button variant="outline" className="border-gray-300 text-gray-900 hover:bg-gray-100 px-8 py-3 text-lg font-semibold" asChild>
              <Link to="/services">Our Services</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Our Sovereign Mission & Accountability Pledge */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Our Sovereign Mission</h2>
              <blockquote className="text-xl text-gray-700 font-serif italic leading-relaxed border-l-4 border-teal-600 pl-6">
                "To provide absolute stability in an ever-shifting landscape. We assume the burden of management so our users can dedicate themselves to the properties that matter most."
              </blockquote>
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">The Accountability Pledge</h2>
              <p className="text-lg text-gray-600 mb-4 leading-relaxed">
                We recognize that institutional-grade property management is not optional. It's essential. This is why we maintain the leverage-intelligence to predict resource needs before they become issues, ensuring transparency, efficiency, and total accountability across every engagement.
              </p>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-teal-600 rounded-full"></div>
                <p className="text-sm font-semibold text-gray-700">Guaranteed Oversight 24/7</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Managed Digital Infrastructure */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Managed Digital<br />Infrastructure</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-8 border border-gray-200">
              <div className="bg-teal-50 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                <BarChart3 className="text-teal-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Predictive Analysis</h3>
              <p className="text-gray-600">
                Machine learning infrastructure for trend analysis and resource optimization. We anticipate maintenance needs before they become expensive crises.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 border border-gray-200">
              <div className="bg-teal-50 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                <Eye className="text-teal-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Curated Partnerships</h3>
              <p className="text-gray-600">
                Strategic relationships with vetted service providers. We maintain the highest standards of adherence and excellence.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 border border-gray-200">
              <div className="bg-teal-50 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                <Lock className="text-teal-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Sovereign Security</h3>
              <p className="text-gray-600">
                Enterprise-grade encryption and data protection with real-time monitoring systems.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why the Industry Follows Us */}
      <section className="py-20 bg-teal-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="bg-gray-300 rounded-lg h-96 flex items-center justify-center">
              <div className="text-gray-500 text-center">
                <div className="text-6xl mb-2">🏢</div>
                <p>Office Interior</p>
              </div>
            </div>
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-8">Why the Industry<br />Follows Us</h2>
              <div className="space-y-6">
                <div>
                  <p className="text-teal-200 text-sm font-semibold uppercase tracking-wide mb-2">01</p>
                  <h3 className="text-xl font-bold mb-2">Predictive Management is Over</h3>
                  <p className="text-teal-100">
                    We've moved beyond reactive to proactive. Anticipating issues before they escalate.
                  </p>
                </div>
                <div>
                  <p className="text-teal-200 text-sm font-semibold uppercase tracking-wide mb-2">02</p>
                  <h3 className="text-xl font-bold mb-2">Total Fiscal Transparency</h3>
                  <p className="text-teal-100">
                    Every part is tracked against invoices and integrated into your reporting dashboard.
                  </p>
                </div>
                <div>
                  <p className="text-teal-200 text-sm font-semibold uppercase tracking-wide mb-2">03</p>
                  <h3 className="text-xl font-bold mb-2">Adaptive Scalability</h3>
                  <p className="text-teal-100">
                    From single properties to massive portfolios. Systems that grow with you.
                  </p>
                </div>
              </div>
              <div className="mt-8 bg-teal-600 px-4 py-3 rounded inline-block">
                <p className="text-sm font-semibold">99.8% Uptime Guarantee</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Principles of the Digital Atelier */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              The Principles of<br />the Digital Atelier.
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center pb-8 border-b-2 border-teal-600">
              <div className="flex justify-center mb-4">
                <div className="text-4xl">✏️</div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">PRECISION</h3>
              <p className="text-gray-600 text-sm">
                Every detail serves a purpose. Exact execution across all touchpoints.
              </p>
            </div>
            <div className="text-center pb-8 border-b-2 border-teal-600">
              <div className="flex justify-center mb-4">
                <div className="text-4xl">👁️</div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">CLARITY</h3>
              <p className="text-gray-600 text-sm">
                Transparent processes. Clear communication. No assumptions.
              </p>
            </div>
            <div className="text-center pb-8 border-b-2 border-teal-600">
              <div className="flex justify-center mb-4">
                <div className="text-4xl">🏛️</div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">LEGACY</h3>
              <p className="text-gray-600 text-sm">
                Built to last. Systems designed for institutional longevity.
              </p>
            </div>
            <div className="text-center pb-8 border-b-2 border-teal-600">
              <div className="flex justify-center mb-4">
                <div className="text-4xl">💡</div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">WISDOM</h3>
              <p className="text-gray-600 text-sm">
                Intelligence at scale. Data-driven decisions for better outcomes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Expanding the Territory */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="bg-gray-300 rounded-lg h-96 flex items-center justify-center">
              <div className="text-gray-500 text-center">
                <div className="text-6xl mb-2">🌍</div>
                <p>City Landscape</p>
              </div>
            </div>
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Expanding the<br />Territory.</h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Virewire is rapidly expanding its service footprint. We're targeting high-value residential segments and commercial investment opportunities.
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Residential Markets:</h4>
                  <ul className="space-y-1 text-gray-600">
                    <li>• North West London</li>
                    <li>• Knightsbridge</li>
                    <li>• Belgravia</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Institutional Focus:</h4>
                  <ul className="space-y-1 text-gray-600">
                    <li>• Chelsea Districts</li>
                    <li>• Mayfair Markets</li>
                    <li>• South West Wealth</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The End of Uncertainty */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">The End of Uncertainty.</h2>
          <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto italic">
            "Do you crave an automation, we restore certainty. Virewire is the difference between having a bad week or having a perfect one."
          </p>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-gray-50 rounded-lg p-8">
              <p className="text-5xl font-bold text-teal-700 mb-2">15k+</p>
              <p className="text-lg text-gray-600">Projects Completed</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-8">
              <p className="text-5xl font-bold text-teal-700 mb-2">4.9/5</p>
              <p className="text-lg text-gray-600">Average Rating</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-8">
              <p className="text-5xl font-bold text-teal-700 mb-2">24h</p>
              <p className="text-lg text-gray-600">Average Response</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-teal-700 text-white">
        <div className="max-w-4xl mx-auto text-center px-8">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Ascend to Managed<br />Intelligence.
          </h2>
          <p className="text-lg text-teal-50 mb-8">
            Virewire is now accepting a limited number of high-net-estates for our 2024 onboarding cycle. Reserve your partnership today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-white text-teal-700 hover:bg-gray-100 px-8 py-3 text-lg font-semibold" asChild>
              <Link to="/services">Request Service</Link>
            </Button>
            <Button variant="outline" className="border-white text-white hover:bg-teal-600 px-8 py-3 text-lg font-semibold" asChild>
              <Link to="/contact">Talk to a Curator</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
    );
  }

  // Database-driven content rendering
  return (
    <Layout>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href="https://virewire.co.uk/about" />
      </Helmet>

      {/* Database Content Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">{pageContent.title}</h1>
          <div 
            className="prose max-w-none text-gray-600 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: pageContent.content }}
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-700 text-white">
        <div className="max-w-4xl mx-auto text-center px-8">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Ready to Partner?
          </h2>
          <p className="text-lg text-teal-50 mb-8">
            Experience the Virewire difference today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-white text-teal-700 hover:bg-gray-100 px-8 py-3 text-lg font-semibold" asChild>
              <Link to="/services">Request Service</Link>
            </Button>
            <Button variant="outline" className="border-white text-white hover:bg-teal-600 px-8 py-3 text-lg font-semibold" asChild>
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AboutPage;

